import Container from "@/components/Container/Container.tsx";
import { Footer } from "@/components/Footer/Footer.tsx";
import { LanguagePicker } from "@/components/Inputs/LanguagePicker/LanguagePicker.tsx";
import { LegalModal } from "@/components/LegalModal/LegalModal.tsx";
import { Logo } from "@/components/Logo/Logo.tsx";
import { MobileCartBar } from "@/components/MobileCartBar/MobileCartBar.tsx";
import { useGTMPage } from "@/helpers/gtm/useGTMPage.ts";
import { AccommodationPicking } from "@/pages/AccommodationPicking/AccommodationPicking.tsx";
import { useURLParams } from "@/pages/AccommodationPicking/hooks/useURLParams.ts";
import { useCheckAndClearDataPreservation } from "@/pages/BookingEngine/hooks/useCheckAndClearDataPreservation.ts";
import { useHolderInfoForm } from "@/pages/BookingEngine/hooks/useHolderInfoForm.ts";
import { usePaymentForm } from "@/pages/BookingEngine/hooks/usePaymentForm.ts";
import { ExtraOptions } from "@/pages/ExtraOptions/ExtraOptions.tsx";
import { PaymentResult } from "@/pages/PaymentResult/PaymentResult.tsx";
import { HolderAndPaymentInfo } from "@/pages/ReservationHolderInformation/HolderAndPaymentInfo.tsx";
import { ReservationOverview } from "@/pages/ReservationOverview/ReservationOverview.tsx";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

// Main component for rendering the correct page
// does not use router because this is an embedded app
export const BookingEngine = () => {
  const { page, rootAttributes } = useEngineStore(
    useShallow((state) => ({ page: state.page, rootAttributes: state.rootAttributes })),
  );

  const { reservationOfferLoading } = useURLParams();
  useCheckAndClearDataPreservation();
  useGTMPage();

  const { holderFormData } = useHolderInfoForm();
  const { paymentFormData } = usePaymentForm(holderFormData);

  // will & should hide logo if true alongside language picker
  const hideLogo = useMemo(() => rootAttributes?.[RootAttribute.HideLanguagePicker], [rootAttributes]);

  return (
    <div className="bookingEngine o-booking-engine">
      {(!hideLogo || !rootAttributes?.[RootAttribute.HideTitle]) && (
        <header className="bookingEngineHeader o-booking-engine__header">
          <Container>
            <div className="o-booking-engine__inner">
              {!rootAttributes?.[RootAttribute.HideTitle] && (
                <h1 className="u-h3 o-booking-engine__title">Swen booking engine</h1>
              )}
              {hideLogo ? null : (
                <div className="o-booking-engine__logo">
                  <Logo />
                </div>
              )}
              <div className="o-booking-engine__language-picker">
                <LanguagePicker />
              </div>
            </div>
          </Container>
        </header>
      )}
      {/* NOTE: uncomment & import for development, easier page switching
                import { DEVPageSwitcher } from "./DEVPageSwitcher";
      */}
      {/*<DEVPageSwitcher />*/}
      <div className="bookingEnginePages" id="bookingEnginePages">
        {page === AppPage.AccommodationPicking && <AccommodationPicking />}
        {page === AppPage.ExtraOptions && <ExtraOptions reservationOfferLoading={reservationOfferLoading} />}
        {page === AppPage.HolderAndPaymentInfo && (
          <HolderAndPaymentInfo holderFormData={holderFormData} paymentFormData={paymentFormData} />
        )}
        {page === AppPage.ReservationOverview && (
          <ReservationOverview
            onSubmitComplete={() => {
              paymentFormData.reset();
              holderFormData.reset();
            }}
            onGoBack={paymentFormData.resetCreditCardFields}
          />
        )}
        {page === AppPage.PaymentResult && <PaymentResult />}
      </div>

      <Footer />
      <MobileCartBar
        holderFormData={holderFormData}
        paymentFormData={paymentFormData}
        reservationOfferLoading={reservationOfferLoading}
      />
      <LegalModal />
    </div>
  );
};
